import { Injectable, inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private msalService = inject(MsalService);
  private sanitizer = inject(DomSanitizer);
  roleSubject = new BehaviorSubject<string[]>([]);
  private roleReadySubject = new BehaviorSubject<boolean>(false);
  roleReady$ = this.roleReadySubject.asObservable();
  userNameSubject = new BehaviorSubject<string>('');
  userEmailSubject = new BehaviorSubject<string>('');
  projectNameSubject = new BehaviorSubject<string>('');
  constructor(private authService: MsalService) {
    this.authService.initialize().subscribe();
  }

  inititalize = () => {
    return this.msalService.initialize();
  };

  setRoles = (value: string[]) => {
    this.roleSubject.next(value);
  };

  setUserName(value: string) {
    this.userNameSubject.next(value);
  }

  public get userName(): string {
    return this.userNameSubject.value;
  }

  setUserEmail(value: string) {
    this.userEmailSubject.next(value);
  }

  public get userEmail(): string {
    return this.userEmailSubject.value;
  }

  setProjectName(value: string) {
    this.projectNameSubject.next(value);
  }

  // AuthService snippet
  getUserRoles = async (): Promise<string[]> => {
    const userInfo = await this.getUserInfo();
    if (userInfo) {
      let roles = (userInfo as any).roles || [];

      roles = roles.filter(
        (role: string) =>
          role.toLowerCase() !== 'nc-user' && role.startsWith('nc-')
      );

      this.setRoles(roles);
      this.roleReadySubject.next(true);
      return roles;
    }
    return [];
  };

  hasRole = async (role: string): Promise<boolean> => {
    const roles = await this.getUserRoles();
    return roles.includes(role);
  };

  getUserInfo = async () => {
    const accessToken = await this.getToken();
    if (accessToken) {
      const decodedToken = jwtDecode(accessToken);
      return decodedToken;
    }
    return undefined;
  };

  getUserImage = (): Promise<SafeUrl | null> => {
    return new Promise((resolve, reject) => {
      this.authService.acquireTokenSilent({ scopes: ['User.Read'] }).subscribe({
        next: (result) => {
          const accessToken = result.accessToken;
          fetch(`${environment.graphApi}/photo/$value`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
            .then((res) => {
              if (!res.ok) {
                // 404 = No photo uploaded
                resolve(null);
                return null;
              }
              return res.blob();
            })
            .then((blob) => {
              if (!blob) return; // handled by resolve(null) already
              const url = URL.createObjectURL(blob);
              const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
              resolve(safeUrl);
            })
            .catch((err) => reject(err));
        },
        error: (err) => reject(err),
      });
    });
  };

  getToken = async () => {
    try {
      const account = await this.msalService.instance.acquireTokenSilent({
        scopes: [environment.apiScopeUrl],
      });
      const accessToken = account?.accessToken;
      return accessToken;
    } catch (error) {
      console.warn('Silent token failed, falling back to popup...');
      try {
        await this.msalService.instance.acquireTokenRedirect({
          scopes: [environment.apiScopeUrl],
        });
        return undefined;
      } catch (popupError) {
        console.error('Popup token acquisition failed:', popupError);
        throw popupError;
      }
    }
  };
}
